import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';

/** How far from the pointer the card sits, and how far off the screen edges. */
const CURSOR_OFFSET = 20;
const EDGE_MARGIN = 8;
/** The card's width. */
const CARD_WIDTH = 240;
/**
 * How long the pointer rests on a row before the card shows. A pointer on
 * its way down the list crosses every row; only one it settles on is a
 * hover.
 */
const HOVER_INTENT_MS = 250;

export interface CursorImagePreviewItem {
    src: string;
    alt: string;
}

/**
 * A picture that follows the pointer while a row is hovered — the sourcebook's
 * product beside the cursor, for a list too dense to carry big pictures.
 *
 * The card's position is written straight to the node on each pointer move:
 * coordinates in React state re-render the card for every event the browser
 * can deliver, which is what makes this kind of thing lag behind the cursor.
 * It stays mounted and hidden rather than mounting on hover, so the first
 * pointer move has a node to position and the card cannot appear at the top
 * left corner for a frame on its way to the cursor.
 *
 * The card waits a beat before it shows, so a pointer travelling down the
 * list does not flash every row's picture on the way past.
 *
 * Returns handlers to spread onto each row, and the card to render once
 * beside the list.
 */
export const useCursorImagePreview = () => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [item, setItem] = useState<CursorImagePreviewItem | null>(null);
    const intentRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearIntent = () => {
        if (intentRef.current !== null) {
            clearTimeout(intentRef.current);
            intentRef.current = null;
        }
    };

    const place = useCallback((pointerX: number, pointerY: number) => {
        const card = cardRef.current;
        if (!card) return;
        const { offsetWidth: width, offsetHeight: height } = card;
        // Beside the pointer and centred on it, so the card reads as attached
        // to the cursor rather than hanging off its corner.
        let left = pointerX + CURSOR_OFFSET;
        let top = pointerY - height / 2;
        // Flip to the pointer's other side rather than hanging off the screen.
        if (left + width > window.innerWidth - EDGE_MARGIN) {
            left = pointerX - width - CURSOR_OFFSET;
        }
        // Held inside the screen top and bottom, which is the one case where
        // it stops tracking the pointer exactly.
        top = Math.min(
            Math.max(EDGE_MARGIN, top),
            Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN),
        );
        card.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
    }, []);

    const bindRow = useCallback(
        (row: CursorImagePreviewItem | null) => {
            if (!row) return {};
            return {
                onMouseEnter: (event: ReactMouseEvent) => {
                    place(event.clientX, event.clientY);
                    clearIntent();
                    intentRef.current = setTimeout(() => {
                        intentRef.current = null;
                        setItem(row);
                    }, HOVER_INTENT_MS);
                },
                onMouseMove: (event: ReactMouseEvent) => {
                    place(event.clientX, event.clientY);
                },
                onMouseLeave: () => {
                    clearIntent();
                    setItem(null);
                },
            };
        },
        [place],
    );

    const preview = (
        <div
            ref={cardRef}
            aria-hidden="true"
            style={{ width: CARD_WIDTH }}
            className={cn(
                'pointer-events-none fixed top-0 left-0 z-tooltip overflow-hidden rounded-[0.75rem] bg-[#f5f5f7]',
                'shadow-[0_1rem_2.5rem_rgba(0,0,0,0.35)]',
                'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                item ? 'opacity-100' : 'opacity-0',
            )}
        >
            {item ? (
                <img
                    src={item.src}
                    alt=""
                    className="aspect-square w-full object-cover"
                />
            ) : (
                <div className="aspect-square w-full" />
            )}
        </div>
    );

    return { bindRow, preview };
};
